import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Space, Row, Modal } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import type {
  SorterResult,
  TablePaginationConfig,
} from 'antd/es/table/interface';
import { Dayjs } from 'dayjs';
import { ParsedUrlQuery } from 'querystring';
import ContainerWrapper from 'Common/Components/ContainerWrapper';
import {
  Cursor,
  EmployeeReportTableParams,
  EmployeeReport as EmployeeReportType,
} from 'utils/apiClient/services/reports/types';
import CursorPagination from 'ShorePay/components/CursorPagination';
import {
  useEmployeeExcelReport,
  useEmployeeReports,
  useUserTransactions,
} from 'Reports/hooks/employee-reports';
import EmployeeTransactionTable from 'Reports/components/views/EmployeeTransactionTable';
import EmployeeReportHeader from './views/EmployeeReportHeader';
import EmployeeReportTable from './views/EmployeeReportTable';

export type EmployeesReportProps = {
  query: ParsedUrlQuery;
  dateFilter: Dayjs[];
};

const EmployeeReport: React.FC<EmployeesReportProps> = ({
  dateFilter,
  query,
}) => {
  const intl = useIntl();
  const [startDate, endDate] = useMemo(() => dateFilter, [dateFilter]);
  const [tableParams, setTableParams] = useState<EmployeeReportTableParams>({
    filters: {
      start_date: startDate,
      end_date: endDate,
    },
    limit: 10,
    cursor: null,
    sorter: null,
  });
  const enabledFlag = useMemo(() => query._ === '8', [query]);

  const employeeReportsQuery = useEmployeeReports({
    tableParams,
    enabled: enabledFlag,
  });

  const [selectedUser, setSelectedUser] = useState<EmployeeReportType | null>(
    null
  );
  const [transactionsCursor, setTransactionsCursor] = useState<string | null>(
    null
  );

  const { data: transactionsData, isLoading: isLoadingTransactions } =
    useUserTransactions({
      userId: selectedUser?.user_id,
      startDate: tableParams.filters.start_date,
      endDate: tableParams.filters.end_date,
      cursor: transactionsCursor,
      enabled: !!selectedUser,
      limit: tableParams.limit,
    });

  const createExcelReport = useEmployeeExcelReport({
    report_name: 'employees',
    start_date: tableParams.filters.start_date,
    end_date: tableParams.filters.end_date,
  });

  const onHandleDownloadEmployeeReport = () => {
    createExcelReport.refetch();
  };

  const updateListTableParams = useCallback(() => {
    setTableParams((prevTableParams) => ({
      ...prevTableParams,
      filters: {
        ...prevTableParams.filters,
        start_date: startDate,
        end_date: endDate,
      },
    }));
  }, [startDate, endDate]);

  useEffect(() => {
    updateListTableParams();
  }, [updateListTableParams]);

  const handleListTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter:
      | SorterResult<EmployeeReportType>
      | SorterResult<EmployeeReportType>[]
  ) => {
    if (sorter && !Array.isArray(sorter)) {
      setTableParams((prev) => ({
        ...prev,
        sorter: {
          columnKey: sorter.columnKey as string,
          order: sorter.order as 'ascend' | 'descend' | null,
        },
      }));
    }
  };

  const handleUserClick = (record: EmployeeReportType) => {
    setSelectedUser(record);
    setTransactionsCursor(null);
  };

  return (
    <Space direction="vertical" css="width: 100%; overflow:auto">
      <ContainerWrapper
        css={{
          height: '20%',
          minHeight: '0 !important',
          padding: '1em',
        }}
      >
        <EmployeeReportHeader
          title="Revenue per Employee"
          handleDownload={onHandleDownloadEmployeeReport}
          downloadInProgress={createExcelReport.isLoading}
          isDisabled={!employeeReportsQuery.data?.results?.length}
          helpCenterLink={
            intl.locale.toLowerCase() === 'de'
              ? 'https://help.shore.com/de/umsatzbericht-pro-mitarbeiter-web-ios'
              : 'https://help.shore.com/en/employee-revenue-report-web-ios'
          }
        />
        <div style={{ overflowX: 'auto' }}>
          <EmployeeReportTable
            data={employeeReportsQuery.data?.results || []}
            isLoading={employeeReportsQuery.isLoading}
            handleListTableChange={handleListTableChange}
            handleUserClick={handleUserClick}
          />
        </div>
        <Modal
          title={
            <FormattedMessage
              defaultMessage="Items sold by {name}"
              values={{ name: selectedUser?.name }}
            />
          }
          open={!!selectedUser}
          onCancel={() => setSelectedUser(null)}
          footer={null}
          width={800}
        >
          <EmployeeTransactionTable
            data={transactionsData}
            isLoading={isLoadingTransactions}
            onCursorChange={setTransactionsCursor}
          />
        </Modal>
        <Row justify="end" css={{ marginTop: '5px' }}>
          <CursorPagination
            previous={employeeReportsQuery.data?.previous}
            next={employeeReportsQuery.data?.next}
            onChange={(cursor: Cursor) => {
              setTableParams((prev) => ({
                ...prev,
                cursor,
              }));
            }}
          />
        </Row>
      </ContainerWrapper>
    </Space>
  );
};

export default EmployeeReport;
